import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../lib/auth-context';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Loader2, Mail, Lock, RadioTower, ArrowRight, Eye, EyeOff } from 'lucide-react';

const Login = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [formData, setFormData] = useState({ login: '', password: '' });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.login || !formData.password) { toast.error('All fields required'); return; }
    setLoading(true);
    try {
      await login(formData.login, formData.password);
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Left side - Form */}
      <div className="flex-1 flex items-center justify-center px-6 py-12 bg-white relative">
        <div className="w-full max-w-[420px] relative z-10">
          {/* Brand */}
          <div className="flex items-center gap-3 mb-10">
            <div className="w-11 h-11 rounded-xl flex items-center justify-center bg-gradient-to-br from-[#4680ff] to-[#2563eb] shadow-lg shadow-blue-500/25">
              <RadioTower className="w-5 h-5 text-white" strokeWidth={2} />
            </div>
            <div className="leading-none">
              <span className="text-[17px] font-bold text-slate-900 tracking-wide block" style={{ fontFamily: 'Outfit, sans-serif' }}>TRIPATHY</span>
              <span className="text-[10px] font-bold text-orange-500 tracking-[0.2em]">TELECOM</span>
            </div>
          </div>

          <div className="mb-8">
            <h1 className="text-3xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>Welcome back</h1>
            <p className="text-slate-500 mt-2 text-sm">Sign in to your account to continue</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Username / Email / Phone</Label>
              <div className="relative">
                <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  type="text" placeholder="Username, email or phone"
                  value={formData.login}
                  onChange={(e) => setFormData({ ...formData, login: e.target.value })}
                  className="pl-11 h-11 bg-slate-50/80 border-slate-200 rounded-lg focus:ring-2 focus:ring-[#4680ff]/20 focus:border-[#4680ff] text-sm"
                  data-testid="login-email"
                />
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Password</Label>
                <Link to="/forgot-password" className="text-xs font-semibold text-[#4680ff] hover:text-[#2563eb] transition-colors" data-testid="forgot-password-link">Forgot password?</Link>
              </div>
              <div className="relative">
                <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  type={showPw ? 'text' : 'password'} placeholder="Enter your password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="pl-11 pr-11 h-11 bg-slate-50/80 border-slate-200 rounded-lg focus:ring-2 focus:ring-[#4680ff]/20 focus:border-[#4680ff] text-sm"
                  data-testid="login-password"
                />
                <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors">
                  {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <Button
              type="submit" disabled={loading}
              className="w-full h-11 text-sm font-semibold rounded-lg text-white bg-[#4680ff] hover:bg-[#2563eb] shadow-lg shadow-blue-500/20 transition-all duration-200 hover:shadow-xl"
              data-testid="login-submit"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><span>Sign In</span><ArrowRight className="w-4 h-4 ml-2" /></>}
            </Button>
          </form>

          <p className="text-center mt-8 text-sm text-slate-500">
            Don't have an account?{' '}
            <Link to="/register" className="font-semibold text-[#4680ff] hover:text-[#2563eb] transition-colors" data-testid="register-link">Register</Link>
          </p>
        </div>

        {/* Developer by Supro - Login page */}
        <div className="absolute bottom-6 left-0 right-0 text-center">
          <p className="text-xs text-slate-400 font-medium tracking-wide opacity-70 hover:opacity-100 transition-opacity" data-testid="login-branding">
            Developer by <span className="font-semibold text-slate-500">Supro</span>
          </p>
        </div>
      </div>

      {/* Right side - Branding visual */}
      <div className="hidden lg:flex lg:w-[45%] relative overflow-hidden items-center justify-center"
        style={{ background: 'linear-gradient(160deg, #0f172a 0%, #1e293b 50%, #0f172a 100%)' }}>
        {/* Abstract shapes */}
        <div className="absolute inset-0">
          <div className="absolute top-20 right-20 w-72 h-72 rounded-full bg-[#4680ff]/10 blur-3xl" />
          <div className="absolute bottom-32 left-16 w-56 h-56 rounded-full bg-orange-500/8 blur-3xl" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 rounded-full border border-white/[0.04]" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] rounded-full border border-white/[0.03]" />
        </div>

        <div className="relative z-10 px-12 text-center max-w-md">
          <div className="w-20 h-20 rounded-2xl bg-[#4680ff]/10 backdrop-blur-sm flex items-center justify-center mx-auto mb-8 ring-1 ring-[#4680ff]/20 shadow-2xl">
            <RadioTower className="w-10 h-10 text-[#4680ff]" strokeWidth={1.5} />
          </div>
          <h1 className="text-4xl font-bold text-white tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>TRIPATHY<br /><span className="text-orange-400">TELECOM</span></h1>
          <p className="mt-4 text-white/40 text-sm leading-relaxed max-w-sm mx-auto">India's trusted platform for instant mobile recharge, DTH & bill payments</p>

          <div className="mt-12 flex items-center justify-center gap-8">
            <div className="text-center">
              <p className="text-2xl font-bold text-white/90 font-mono">10K+</p>
              <p className="text-[10px] text-white/30 uppercase tracking-wider font-medium mt-1">Retailers</p>
            </div>
            <div className="w-px h-10 bg-white/[0.08]" />
            <div className="text-center">
              <p className="text-2xl font-bold text-white/90 font-mono">50L+</p>
              <p className="text-[10px] text-white/30 uppercase tracking-wider font-medium mt-1">Recharges</p>
            </div>
            <div className="w-px h-10 bg-white/[0.08]" />
            <div className="text-center">
              <p className="text-2xl font-bold text-white/90 font-mono">99.9%</p>
              <p className="text-[10px] text-white/30 uppercase tracking-wider font-medium mt-1">Uptime</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
